import { DATA_COVERAGE, isCovered } from '@/config/constants';
import { DATA_SOURCES } from 'utils/metrics';
import { Link } from 'react-router-dom';
import NoDataSection from './NoDataSection';
import SerialComma from './SerialComma';
import { cn } from '@/ui/design-system/utils/cn';
import { useCountryParam } from 'utils/hooks';

const DATA_SOURCE_LINK_CLASS_NAME = cn(
    'p-0',
    'text-sm',
    'font-medium',
    'hover:underline',
);

// Map data source keys to route paths
const DATA_SOURCE_ROUTES = {
    GOOGLE: 'google',
    TIKTOK: 'tiktok',
    INSTAGRAM: 'instagram',
};

const formatDataSourceLabel = (dataSourceKey) => {
    if (!dataSourceKey) return 'data';
    const normalized = String(dataSourceKey).toUpperCase();
    return DATA_SOURCES[normalized]?.label || 'data';
};

function Message({ dataSource, country, industryName }) {
    const { mutateToWithCountryParam } = useCountryParam();
    const label = formatDataSourceLabel(dataSource);

    // Get available data sources based on coverage
    const availableDataSources = Object.keys(DATA_SOURCES)
        .filter((dsKey) => {
            // Don't suggest the current failing data source
            if (dsKey === dataSource) return false;

            // Check if data source is covered for this market
            if (!DATA_COVERAGE[country?.text_id]?.[dsKey]) return false;

            // Check if data source is covered for this category
            if (
                industryName &&
                !isCovered({
                    geo: country?.text_id,
                    dataSource: dsKey,
                    cat1: industryName,
                })
            )
                return false;

            return true;
        })
        .map((dsKey) => ({
            key: dsKey,
            label: formatDataSourceLabel(dsKey),
            route: DATA_SOURCE_ROUTES[dsKey],
        }));

    if (availableDataSources.length === 0) {
        return (
            <>
                Could not get {label} data. No alternative data sources are
                available for this market and industry.
            </>
        );
    }

    return (
        <>
            Could not get {label} data. Try switching to{' '}
            <SerialComma conjunction="or">
                {availableDataSources.map(({ key, label: altLabel, route }) => {
                    const path = route
                        ? `/industries/${industryName}/${route}`
                        : `/industries/${industryName}`;

                    return (
                        <Link
                            key={key}
                            to={mutateToWithCountryParam(path)}
                            className={DATA_SOURCE_LINK_CLASS_NAME}
                        >
                            {altLabel}
                        </Link>
                    );
                })}
            </SerialComma>
            .
        </>
    );
}

function DataSourceQueryError({ dataSource, country, industryName, ...props }) {
    return (
        <NoDataSection
            heading="Unable to load data"
            message={
                <Message
                    dataSource={dataSource}
                    country={country}
                    industryName={industryName}
                />
            }
            {...props}
        />
    );
}

export default DataSourceQueryError;
